import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const SettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(i18n.language);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    toast.success(t('settings.languageChanged'));
  };

  const handleLogout = async () => {
    if (!window.confirm(t('settings.logoutConfirm'))) {
      return;
    }
    try {
      await logout();
      navigate('/login');
      toast.success(t('auth.logout'));
    } catch (error) {
      toast.error(t('settings.logoutError'));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/profile')}
        className="mb-4 text-primary-600 hover:text-primary-700"
      >
        ← {t('common.back')}
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('profile.settings')}</h1>

      {/* Language Settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('settings.language')}</h2>
        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="language"
              value="en"
              checked={language === 'en'}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700">English</span>
          </label>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="language"
              value="es"
              checked={language === 'es'}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700">Español</span>
          </label>
        </div>
      </div>

      {/* Account Settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('settings.account')}</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium text-gray-900">{t('settings.email')}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium text-gray-900">{t('profile.loyaltyNumber')}</p>
              <p className="text-sm text-gray-500">{user?.loyaltyNumber}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('settings.actions')}</h2>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/profile')}
            className="w-full px-4 py-2 text-left border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
          >
            {t('profile.editProfile')}
          </button>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-left border border-red-300 rounded-md hover:bg-red-50 text-red-600"
          >
            {t('auth.logout')}
          </button>
        </div>
      </div>

      {/* App Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('settings.about')}</h2>
        <div className="space-y-2 text-sm text-gray-600">
          <p>RideConnect v1.0.0</p>
          <p>{t('settings.appDescription')}</p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
